<template>
  <b-card no-body>
    <!-- Head -->
    <table-header
      search
      search-placeholder="Cari berdasarkan kode"
      search-action="voucher/getVoucherList"
    >
      <template #action>
        <b-button
          v-if="canAccess('manage', 'Voucher')"
          variant="primary"
          :to="{ name: 'vouchers-create'}"
        >
          Buat Voucher
        </b-button>
      </template>
    </table-header>

    <!-- Body -->
    <b-skeleton-table
      v-if="loading"
      :rows="5"
      :columns="7"
    />

    <b-table
      v-else
      ref="refVoucherListTable"
      :items="vouchers.data"
      responsive
      :fields="tableFields"
      primary-key="id"
      show-empty
      empty-text="Tidak ada data yang ditemukan"
      class="position-relative mh-160"
    >
      <!-- Column: Kode -->
      <template #cell(code)="data">
        {{ data.item.code || '-' }}
      </template>

      <!-- Column: Status -->
      <template #cell(status)="data">
        <b-badge
          v-if="data.item.is_active === 1"
          pill
          variant="light-success"
        >
          Tersedia
        </b-badge>
        <b-badge
          v-else
          pill
          variant="light-danger"
        >
          Tidak Tersedia
        </b-badge>
        <b-badge
          v-if="data.item.is_claimable === 1"
          pill
          variant="light-primary"
          class="ml-1"
        >
          Claimable
        </b-badge>
      </template>

      <!-- Column: Diskon -->
      <template #cell(discount)="data">
        <span class="text-nowrap">
          {{ `${data.item.discount}% maks. ${toCurrency(data.item.max_discount)}` }}
        </span>
      </template>

      <!-- Column: Jenis Potongan -->
      <template #head(type)="scope">
        <div class="text-nowrap">
          {{ scope.label }}
        </div>
      </template>
      <template #cell(type)="data">
        {{ voucherType(data.item.type) }}
      </template>

      <!-- Column: Telah Digunakan -->
      <template #head(used)="scope">
        <div class="text-nowrap">
          {{ scope.label }}
        </div>
      </template>
      <template #cell(used)="data">
        {{ `${toThousands(data.item.used)} dari ${toThousands(data.item.max_usage_all)}` || '-' }}
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <table-action
            :actions="tableAction"
            :data="data"
          />
        </div>
      </template>

    </b-table>

    <div class="mx-2 mb-2">
      <pagination
        :data="vouchers.meta"
        action="voucher/getVoucherList"
      />
    </div>

    <b-modal
      v-model="downloadQrCodeModal"
      title="Download QR Code"
      ok-only
      ok-title="Download"
      :ok-disabled="isPending"
      :cancel-disabled="isPending"
      @ok="onDownloadQrCode"
    >
      <b-card-text
        v-if="dataToDownlaod"
        class="text-center"
      >
        <qrcode-vue
          ref="qrCodeArea"
          :value="dataToDownlaod.value"
          :size="qrSize"
          level="H"
        />
      </b-card-text>
    </b-modal>
  </b-card>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import QrcodeVue from 'qrcode.vue'
import {
  BBadge,
  BCard,
  BSkeletonTable,
  BTable,
  BCardText,
  BButton,
} from 'bootstrap-vue'
import Pagination from '@/layouts/components/custom/Pagination.vue'
import TableHeader from '@/layouts/components/custom/TableHeader.vue'
import TableAction from '@/layouts/components/custom/TableAction.vue'

export default {
  components: {
    BBadge,
    BCard,
    BSkeletonTable,
    BTable,
    BCardText,
    BButton,
    QrcodeVue,
    Pagination,
    TableHeader,
    TableAction,
  },
  data() {
    return {
      tableFields: [
        { key: 'name', label: 'Voucher' },
        { key: 'code', label: 'Kode' },
        { key: 'status', label: 'Status' },
        { key: 'discount', label: 'Diskon' },
        { key: 'type', label: 'Jenis Potongan' },
        { key: 'used', label: 'Digunakan' },
        { key: 'actions', label: '' },
      ],
      isPending: false,
      downloadQrCodeModal: false,
      dataToDownlaod: '',
      qrSize: 450,
    }
  },
  computed: {
    ...mapState('voucher', ['vouchers', 'loading']),
    query() {
      return this.$route.query
    },
    tableAction() {
      return [
        {
          access: this.canAccess('manage', 'Voucher'),
          to: 'vouchers-edit|id:id',
          action: '',
          icon: 'EditIcon',
          title: 'Edit',
        },
        {
          access: null,
          to: 'vouchers-poster|id:id',
          action: '',
          icon: 'PrinterIcon',
          title: 'Print Poster',
        },
        {
          access: this.canAccess('read', 'Transaction') || this.canAccess('manage', 'Transaction'),
          to: 'transactions||voucher:code',
          action: '',
          icon: 'PrinterIcon',
          title: 'Lihat Transaksi',
        },
        {
          access: null,
          to: null,
          action: data => { this.openToDownlaod(data) },
          icon: 'DownloadIcon',
          title: 'Unduh QR Code',
        },
      ]
    },
  },
  async created() {
    await this.getVoucherList(this.query)
  },
  methods: {
    ...mapActions('voucher', ['getVoucherList']),
    voucherType(type) {
      switch (type) {
        case 'shipping':
          return 'Ongkir'
        case 'regular':
          return 'Total Harga'
        default:
          return 'Tipe Belum Terdaftar'
      }
    },
    openToDownlaod(data) {
      this.dataToDownlaod = {
        ...data,
        value: `${process.env.VUE_APP_CLIENT_URL}/voucher/claim/${data.id}`,
      }
      this.downloadQrCodeModal = true
    },
    async onDownloadQrCode() {
      this.isPending = true
      const el = this.$refs.qrCodeArea.$refs['qrcode-vue']

      const options = {
        type: 'dataURL',
      }
      const printCanvas = await this.$html2canvas(el, options)

      const link = document.createElement('a')
      link.setAttribute('download', `${this.dataToDownlaod.code}.png`)
      link.setAttribute(
        'href',
        printCanvas,
      )
      link.click()
      this.isPending = false
    },
  },
}
</script>
